import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Tooltip
} from '@mui/material';
import {
  LocalShipping,
  AttachMoney,
  Schedule,
  Map,
  Refresh,
  Place,
  CalendarToday,
  Search,
  Scale,
  Balance,
  Inventory
} from '@mui/icons-material';
import GavelIcon from '@mui/icons-material/Gavel';

import { styled } from '@mui/material/styles';
import { OpenInFull, Close as CloseIcon } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { GetBookedShipments } from '../../apis/Agency';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from 'react-meta-tags';
import OriginDestinationTab from './OriginDestinationTab';
import OnTimeDeliveryTab from './OnTimeDeliveryTab';
import WeeklyTrendsTab from './WeeklyTrendsTab';
import ClaimsDashboard from '../CarrierClaimForm/ClaimsDashboard';
import moment from 'moment-timezone';
import YOYFreightTab from './YOYFreightTab';

// Import new components
import FreightSpendTab from './FreightSpendTab';
import ShipmentsTab from './ShipmentsTab';
import FilterBar from './FilterBar';
// import PalletScoreCard from './PalletScoreCard';
import PalletLocationBreakdown from './PalletLocationBreakdown';

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  minHeight: '60vh',
  maxHeight: '90vh',
  padding: theme.spacing(1.5),
  paddingBottom: '1px',
  overflow: 'auto'
}));

const CompactTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '28px',
  display: 'flex',
  '& .MuiTab-root': {
    minHeight: '48px',
    padding: '8px 12px',
    fontSize: '0.70rem',
    fontWeight: 500,
    '& .MuiTab-iconWrapper': {
      marginBottom: '2px',
      fontSize: '18px'
    }
  }
}));

const TARGET_TIMEZONE = 'America/New_York';

// Consistent date parsing function
const parseToESTDate = dateString => {
  if (!dateString) return null;
  return moment.tz(dateString, TARGET_TIMEZONE).startOf('day').toDate();
};

// Consistent comparison function
const isDateInRangeEST = (dateString, startDate, endDate) => {
  const date = parseToESTDate(dateString);
  const start = parseToESTDate(startDate);
  const end = parseToESTDate(endDate);

  if (!date || !start || !end) return false;
  return date >= start && date <= end;
};

const TMSDashboard = () => {
  const AgencyID = useSelector(state => state.userDetails.agencyLoginID);
  const [activeTab, setActiveTab] = useState(0);
  const [shipsData, setShipsData] = useState([]);
  const [expandedChart, setExpandedChart] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [bookedShipments, setBookedShipments] = useState([]);
  const [originalBookedShipments, setOriginalBookedShipments] = useState([]);
  const [filters, setFilters] = useState({
    customer: 'All',
    originState: 'All',
    destinationState: 'All',
    shipmentType: 'All',
    shipmentTypeRule: 'All',
    carrier: 'All',
    dateRange: 'custom',
    startDate: new Date('2024-10-28').toISOString().split('T')[0],
    endDate: new Date('2026-01-21').toISOString().split('T')[0],
    week: 'All',
    selectedMonth: 'All',
    selectedWeightRange: 'All',
    selectedMileRange: 'All',
    selectedCarrier: 'All',
    selectedShipmentType: 'All'
  });
  const [userPermissionsArr, setUserPermissionsArr] = useState(null);

  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const userRolePermissions = useSelector(
    state => state?.userDetails?.userRolePermissionsArr
  );

  useEffect(() => {
    const filteredCarrierPermissions = userRolePermissions?.filter(
      items => items.permissionName === 'Dashboard'
    );
    setUserPermissionsArr(filteredCarrierPermissions);
  }, [userRolePermissions]);
  const UserIsAdmin = useSelector(state => state?.userDetails?.isAdmin);

  useEffect(() => {
    GetBookedShipments(AgencyID)
      .then(res => {
        if (res.status === 200) {
          const allowedStatuses = ['InTransit', 'Delivered'];

          const COMPANY_KEYS = [
            'carrierName',
            'leastCostCarrierName',
            'leastTransitCarrierName',
            'tlCarrierName',
            'originCompany',
            'destinationCompany',
            'billToCompany',
            'carrierBillToCompany'
          ];

          function smartNormalizeName(raw) {
            if (raw === null || raw === undefined) return raw;
            let s = String(raw).replace(/\s+/g, ' ').trim();
            s = s.replace(/^[\s\p{P}]+|[\s\p{P}]+$/gu, '');

            if (!s) return s;

            const tokens = s.split(' ').map(token => {
              const cleaned = token.replace(
                /^[^\p{L}\p{N}&-/.]+|[^\p{L}\p{N}&-/.]+$/gu,
                ''
              );

              if (
                cleaned &&
                cleaned === cleaned.toUpperCase() &&
                cleaned.length <= 4
              ) {
                return cleaned;
              }

              if (cleaned.includes('.') || cleaned.includes('/')) {
                return cleaned
                  .split(/([./])/)
                  .map(part => {
                    if (part === '.' || part === '/') return part;
                    return (
                      part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
                    );
                  })
                  .join('');
              }

              const subParts = cleaned.split(/([-'])/);
              return subParts
                .map(sp => {
                  if (sp === '-' || sp === "'") return sp;
                  return sp.length === 0
                    ? sp
                    : sp.charAt(0).toUpperCase() + sp.slice(1).toLowerCase();
                })
                .join('');
            });

            return tokens.join(' ').replace(/\s+/g, ' ').trim();
          }

          const normalizeCarrierName = carrierName => {
            if (!carrierName) return carrierName;

            const name = carrierName.toString().toLowerCase().trim();

            if (name.includes('xpo')) {
              return 'XPO';
            }

            return smartNormalizeName(carrierName);
          };

          const filteredShips = res.data.data
            .filter(shipment =>
              allowedStatuses.includes(shipment.shipmentStatus)
            )
            .map(shipment => {
              const copy = { ...shipment };

              COMPANY_KEYS.forEach(key => {
                if (
                  Object.prototype.hasOwnProperty.call(copy, key) &&
                  copy[key] != null
                ) {
                  if (
                    key === 'CarrierName' ||
                    key === 'carrierName' ||
                    key === 'carrier'
                  ) {
                    copy[key] = normalizeCarrierName(copy[key]);
                  } else {
                    copy[key] = smartNormalizeName(copy[key]);
                  }
                }
              });

              return copy;
            });

          const bookedShips = res.data.data
            .filter(shipment => shipment.shipmentStatus === 'Booked')
            .map(shipment => {
              const copy = { ...shipment };

              COMPANY_KEYS.forEach(key => {
                if (
                  Object.prototype.hasOwnProperty.call(copy, key) &&
                  copy[key] != null
                ) {
                  copy[key] = smartNormalizeName(copy[key]);
                }
              });

              return copy;
            });

          setBookedShipments(bookedShips);
          setOriginalBookedShipments(bookedShips);
          setShipsData(filteredShips);
          setShipments(filteredShips);
        }
      })
      .catch(err => console.log('Error in Booked Shipments: ', err))
      .finally(() => setLoading(false));
  }, [AgencyID]);

  const createDateWithoutTime = dateString => {
    if (!dateString) return null;

    const m = moment.tz(dateString, 'America/New_York');

    if (!m.isValid()) return null;

    return m.startOf('day').toDate();
  };

  // Enhanced filter handler with chart click support
  // Enhanced filter handler with chart click support
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);

    // Filter main shipments
    let filteredData = shipsData;

    // Apply all basic filters to main shipments
    if (newFilters.customer !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.destinationCompany === newFilters.customer
      );
    }

    if (newFilters.originState !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.originState === newFilters.originState
      );
    }

    if (newFilters.destinationState !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.destinationState === newFilters.destinationState
      );
    }

    if (newFilters.shipmentType !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.shipmentType === newFilters.shipmentType
      );
    }

    if (newFilters.shipmentTypeRule !== 'All') {
      filteredData = filteredData.filter(
        shipment =>
          shipment.shipmentTypeRule.trim().toLowerCase() ===
          newFilters.shipmentTypeRule.toLowerCase()
      );
    }

    if (newFilters.carrier !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.carrierName === newFilters.carrier
      );
    }

    // Apply chart click filters to main shipments
    if (newFilters.selectedMonth !== 'All') {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const [monthAbbr, yearSuffix] = newFilters.selectedMonth.split('-');
      const monthIndex = monthNames.indexOf(monthAbbr);
      const year = 2000 + parseInt(yearSuffix);

      if (monthIndex !== -1) {
        filteredData = filteredData.filter(shipment => {
          const dateField =
            activeTab === 4 ? 'actualDeliveryDate' : 'actualPickupDate';

          if (!shipment[dateField]) return false;
          const date = new Date(shipment[dateField]);
          return date.getMonth() === monthIndex && date.getFullYear() === year;
        });
      }
    }

    if (newFilters.selectedWeightRange !== 'All') {
      const weightRanges = {
        '<500': { min: 0, max: 499 },
        '500-999': { min: 500, max: 999 },
        '1,000-1,999': { min: 1000, max: 1999 },
        '2,000-4,999': { min: 2000, max: 4999 },
        '5,000-9,999': { min: 5000, max: 9999 },
        '10,000-19,999': { min: 10000, max: 19999 },
        '20,000-40,000': { min: 20000, max: 40000 },
        '>40,000': { min: 40001, max: Infinity }
      };

      const range = weightRanges[newFilters.selectedWeightRange];
      if (range) {
        filteredData = filteredData.filter(shipment => {
          const weight = shipment.totalGrossWeight || 0;
          return weight >= range.min && weight <= range.max;
        });
      }
    }

    if (newFilters.selectedMileRange !== 'All') {
      const mileRanges = {
        '0-49': { min: 0, max: 49 },
        '50-99': { min: 50, max: 99 },
        '100-249': { min: 100, max: 249 },
        '250-499': { min: 250, max: 499 },
        '500-749': { min: 500, max: 749 },
        '750-999': { min: 750, max: 999 },
        '1,000-1,499': { min: 1000, max: 1499 },
        '1,500-1,999': { min: 1500, max: 1999 },
        '2,000-2,499': { min: 2000, max: 2499 },
        '2,500+': { min: 2500, max: Infinity }
      };

      const range = mileRanges[newFilters.selectedMileRange];
      if (range) {
        filteredData = filteredData.filter(shipment => {
          const METERS_PER_MILE = 1609.344;
          const miles = shipment.shipmentMiles
            ? Math.round(shipment.shipmentMiles / METERS_PER_MILE)
            : 0;
          return miles >= range.min && miles <= range.max;
        });
      }
    }

    if (newFilters.selectedCarrier !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.carrierName === newFilters.selectedCarrier
      );
    }

    if (newFilters.selectedShipmentType !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.shipmentType === newFilters.selectedShipmentType
      );
    }

    // Date Range Filter Logic - Apply to main shipments
    if (newFilters.dateRange !== 'All') {
      const dateField =
        activeTab === 4 ? 'actualDeliveryDate' : 'actualPickupDate';

      switch (newFilters.dateRange) {
        case 'last7':
          const sevenDaysAgo = moment()
            .tz('America/New_York')
            .subtract(7, 'days')
            .startOf('day')
            .toDate();

          filteredData = filteredData.filter(shipment => {
            if (!shipment[dateField]) return false;
            const shipmentMoment = moment
              .tz(shipment[dateField], 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= sevenDaysAgo;
          });
          break;

        case 'last30':
          const thirtyDaysAgo = moment()
            .tz('America/New_York')
            .subtract(30, 'days')
            .startOf('day')
            .toDate();

          filteredData = filteredData.filter(shipment => {
            if (!shipment[dateField]) return false;
            const shipmentMoment = moment
              .tz(shipment[dateField], 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= thirtyDaysAgo;
          });
          break;

        case 'last90':
          const ninetyDaysAgo = moment()
            .tz('America/New_York')
            .subtract(90, 'days')
            .startOf('day')
            .toDate();

          filteredData = filteredData.filter(shipment => {
            if (!shipment[dateField]) return false;
            const shipmentMoment = moment
              .tz(shipment[dateField], 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= ninetyDaysAgo;
          });
          break;

        case 'custom':
          if (newFilters.startDate && newFilters.endDate) {
            const startMoment = moment
              .tz(newFilters.startDate, 'America/New_York')
              .startOf('day');
            const endMoment = moment
              .tz(newFilters.endDate, 'America/New_York')
              .endOf('day');

            if (startMoment.isValid() && endMoment.isValid()) {
              const startDate = startMoment.toDate();
              const endDate = endMoment.toDate();

              filteredData = filteredData.filter(shipment => {
                if (!shipment[dateField]) return false;

                const shipmentMoment = moment
                  .tz(shipment[dateField], 'America/New_York')
                  .startOf('day');
                if (!shipmentMoment.isValid()) return false;

                const shipmentDate = shipmentMoment.toDate();

                return shipmentDate >= startDate && shipmentDate <= endDate;
              });
            }
          }
          break;

        default:
          if (newFilters.dateRange !== 'custom') {
            const selectedDateMoment = moment
              .tz(newFilters.dateRange, 'America/New_York')
              .startOf('day');
            if (selectedDateMoment.isValid()) {
              const selectedDate = selectedDateMoment.toDate();

              filteredData = filteredData.filter(shipment => {
                if (!shipment[dateField]) return false;
                const shipmentMoment = moment
                  .tz(shipment[dateField], 'America/New_York')
                  .startOf('day');
                if (!shipmentMoment.isValid()) return false;

                const shipmentDate = shipmentMoment.toDate();
                return (
                  shipmentDate &&
                  shipmentDate.getTime() === selectedDate.getTime()
                );
              });
            }
          }
          break;
      }
    }

    // Week Filter Logic - Apply to main shipments
    if (newFilters.week !== 'All') {
      const dateField =
        activeTab === 4 ? 'actualDeliveryDate' : 'actualPickupDate';

      filteredData = filteredData.filter(shipment => {
        if (!shipment[dateField]) return false;

        const shipmentDate = createDateWithoutTime(shipment[dateField]);
        if (!shipmentDate) return false;

        const [year, weekNum] = newFilters.week.split(' W');

        const getISOWeekNumber = date => {
          const target = new Date(date);
          const dayNumber = (target.getUTCDay() + 6) % 7;
          target.setUTCDate(target.getUTCDate() - dayNumber + 3);
          const firstThursday = target.valueOf();
          target.setUTCMonth(0, 1);
          if (target.getUTCDay() !== 4) {
            target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7));
          }
          return 1 + Math.ceil((firstThursday - target) / 604800000);
        };

        const shipmentWeek = getISOWeekNumber(shipmentDate);
        const shipmentYear = shipmentDate.getUTCFullYear();

        return (
          shipmentYear.toString() === year &&
          shipmentWeek.toString() === weekNum
        );
      });
    }

    // Update main shipments
    setShipments(filteredData);

    // Apply the SAME filters to booked shipments
    let filteredBookedData = originalBookedShipments; // Use the current bookedShipments as base

    // Apply basic filters to booked shipments
    if (newFilters.customer !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.destinationCompany === newFilters.customer
      );
    }

    if (newFilters.originState !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.originState === newFilters.originState
      );
    }

    if (newFilters.destinationState !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.destinationState === newFilters.destinationState
      );
    }

    if (newFilters.shipmentType !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.shipmentType === newFilters.shipmentType
      );
    }

    if (newFilters.shipmentTypeRule !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment =>
          shipment.shipmentTypeRule.trim().toLowerCase() ===
          newFilters.shipmentTypeRule.toLowerCase()
      );
    }

    if (newFilters.carrier !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.carrierName === newFilters.carrier
      );
    }

    // Apply chart click filters to booked shipments
    if (newFilters.selectedMonth !== 'All') {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const [monthAbbr, yearSuffix] = newFilters.selectedMonth.split('-');
      const monthIndex = monthNames.indexOf(monthAbbr);
      const year = 2000 + parseInt(yearSuffix);

      if (monthIndex !== -1) {
        filteredBookedData = filteredBookedData.filter(shipment => {
          // For booked shipments, always use actualDeliveryDate (or pickup date if that's more appropriate)
          const dateField = 'actualDeliveryDate'; // Or 'actualPickupDate' based on your needs

          if (!shipment[dateField]) return false;
          const date = new Date(shipment[dateField]);
          return date.getMonth() === monthIndex && date.getFullYear() === year;
        });
      }
    }

    if (newFilters.selectedWeightRange !== 'All') {
      const weightRanges = {
        '<500': { min: 0, max: 499 },
        '500-999': { min: 500, max: 999 },
        '1,000-1,999': { min: 1000, max: 1999 },
        '2,000-4,999': { min: 2000, max: 4999 },
        '5,000-9,999': { min: 5000, max: 9999 },
        '10,000-19,999': { min: 10000, max: 19999 },
        '20,000-40,000': { min: 20000, max: 40000 },
        '>40,000': { min: 40001, max: Infinity }
      };

      const range = weightRanges[newFilters.selectedWeightRange];
      if (range) {
        filteredBookedData = filteredBookedData.filter(shipment => {
          const weight = shipment.totalGrossWeight || 0;
          return weight >= range.min && weight <= range.max;
        });
      }
    }

    if (newFilters.selectedMileRange !== 'All') {
      const mileRanges = {
        '0-49': { min: 0, max: 49 },
        '50-99': { min: 50, max: 99 },
        '100-249': { min: 100, max: 249 },
        '250-499': { min: 250, max: 499 },
        '500-749': { min: 500, max: 749 },
        '750-999': { min: 750, max: 999 },
        '1,000-1,499': { min: 1000, max: 1499 },
        '1,500-1,999': { min: 1500, max: 1999 },
        '2,000-2,499': { min: 2000, max: 2499 },
        '2,500+': { min: 2500, max: Infinity }
      };

      const range = mileRanges[newFilters.selectedMileRange];
      if (range) {
        filteredBookedData = filteredBookedData.filter(shipment => {
          const METERS_PER_MILE = 1609.344;
          const miles = shipment.shipmentMiles
            ? Math.round(shipment.shipmentMiles / METERS_PER_MILE)
            : 0;
          return miles >= range.min && miles <= range.max;
        });
      }
    }

    if (newFilters.selectedCarrier !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.carrierName === newFilters.selectedCarrier
      );
    }

    if (newFilters.selectedShipmentType !== 'All') {
      filteredBookedData = filteredBookedData.filter(
        shipment => shipment.shipmentType === newFilters.selectedShipmentType
      );
    }

    // Date Range Filter Logic - Apply to booked shipments
    if (newFilters.dateRange !== 'All') {
      // For booked shipments, use actualDeliveryDate consistently
      const dateField = 'actualDeliveryDate';

      switch (newFilters.dateRange) {
        case 'last7':
          const sevenDaysAgo = moment()
            .tz('America/New_York')
            .subtract(7, 'days')
            .startOf('day')
            .toDate();

          filteredBookedData = filteredBookedData.filter(shipment => {
            if (!shipment[dateField]) return false;
            const shipmentMoment = moment
              .tz(shipment[dateField], 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= sevenDaysAgo;
          });
          break;

        case 'last30':
          const thirtyDaysAgo = moment()
            .tz('America/New_York')
            .subtract(30, 'days')
            .startOf('day')
            .toDate();

          filteredBookedData = filteredBookedData.filter(shipment => {
            if (!shipment[dateField]) return false;
            const shipmentMoment = moment
              .tz(shipment[dateField], 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= thirtyDaysAgo;
          });
          break;

        case 'last90':
          const ninetyDaysAgo = moment()
            .tz('America/New_York')
            .subtract(90, 'days')
            .startOf('day')
            .toDate();

          filteredBookedData = filteredBookedData.filter(shipment => {
            if (!shipment[dateField]) return false;
            const shipmentMoment = moment
              .tz(shipment[dateField], 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= ninetyDaysAgo;
          });
          break;

        case 'custom':
          if (newFilters.startDate && newFilters.endDate) {
            const startMoment = moment
              .tz(newFilters.startDate, 'America/New_York')
              .startOf('day');
            const endMoment = moment
              .tz(newFilters.endDate, 'America/New_York')
              .endOf('day');

            if (startMoment.isValid() && endMoment.isValid()) {
              const startDate = startMoment.toDate();
              const endDate = endMoment.toDate();

              filteredBookedData = filteredBookedData.filter(shipment => {
                if (!shipment[dateField]) return false;

                const shipmentMoment = moment
                  .tz(shipment[dateField], 'America/New_York')
                  .startOf('day');
                if (!shipmentMoment.isValid()) return false;

                const shipmentDate = shipmentMoment.toDate();

                return shipmentDate >= startDate && shipmentDate <= endDate;
              });
            }
          }
          break;

        default:
          if (newFilters.dateRange !== 'custom') {
            const selectedDateMoment = moment
              .tz(newFilters.dateRange, 'America/New_York')
              .startOf('day');
            if (selectedDateMoment.isValid()) {
              const selectedDate = selectedDateMoment.toDate();

              filteredBookedData = filteredBookedData.filter(shipment => {
                if (!shipment[dateField]) return false;
                const shipmentMoment = moment
                  .tz(shipment[dateField], 'America/New_York')
                  .startOf('day');
                if (!shipmentMoment.isValid()) return false;

                const shipmentDate = shipmentMoment.toDate();
                return (
                  shipmentDate &&
                  shipmentDate.getTime() === selectedDate.getTime()
                );
              });
            }
          }
          break;
      }
    }

    // Week Filter Logic - Apply to booked shipments
    if (newFilters.week !== 'All') {
      const dateField = 'actualDeliveryDate'; // Use delivery date for booked shipments

      filteredBookedData = filteredBookedData.filter(shipment => {
        if (!shipment[dateField]) return false;

        const shipmentDate = createDateWithoutTime(shipment[dateField]);
        if (!shipmentDate) return false;

        const [year, weekNum] = newFilters.week.split(' W');

        const getISOWeekNumber = date => {
          const target = new Date(date);
          const dayNumber = (target.getUTCDay() + 6) % 7;
          target.setUTCDate(target.getUTCDate() - dayNumber + 3);
          const firstThursday = target.valueOf();
          target.setUTCMonth(0, 1);
          if (target.getUTCDay() !== 4) {
            target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7));
          }
          return 1 + Math.ceil((firstThursday - target) / 604800000);
        };

        const shipmentWeek = getISOWeekNumber(shipmentDate);
        const shipmentYear = shipmentDate.getUTCFullYear();

        return (
          shipmentYear.toString() === year &&
          shipmentWeek.toString() === weekNum
        );
      });
    }

    // Update booked shipments
    setBookedShipments(filteredBookedData);
  };

  // Chart click handlers
  const handleMonthFilter = selectedMonth => {
    handleFilterChange('selectedMonth', selectedMonth);
    if (expandedChart) setExpandedChart(null);
  };

  const handleCarrierFilter = selectedCarrier => {
    handleFilterChange('selectedCarrier', selectedCarrier);
    if (expandedChart) setExpandedChart(null);
  };

  const handleWeightFilter = selectedWeightRange => {
    handleFilterChange('selectedWeightRange', selectedWeightRange);
    if (expandedChart) setExpandedChart(null);
  };

  const handleMileFilter = selectedMileRange => {
    handleFilterChange('selectedMileRange', selectedMileRange);
    if (expandedChart) setExpandedChart(null);
  };

  const getUniqueWeeks = () => {
    const getWeekNumber = date => {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    };

    const weeks = shipsData
      .map(shipment => {
        if (!shipment.actualPickupDate) return null;
        const date = new Date(shipment.actualPickupDate);
        const year = date.getFullYear();
        const week = getWeekNumber(date);
        return `${year} W${week}`;
      })
      .filter(Boolean);

    return ['All', ...new Set(weeks)];
  };

  // Add this optimized data calculation before your return statement
  const monthlyShipmentTypeData = React.useMemo(() => {
    const dataByMonthType = {};
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.shipmentType) return;

      const date = new Date(shipment.actualPickupDate);

      const month = monthNames[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonthType[monthYear]) {
        dataByMonthType[monthYear] = { LTL: 0, TL: 0, Parcel: 0 };
      }

      const type = shipment.shipmentType;
      if (dataByMonthType[monthYear][type] !== undefined) {
        dataByMonthType[monthYear][type] += shipment.quoteNetCharge || 0;
      }
    });

    const allMonths = Object.keys(dataByMonthType).sort((a, b) => {
      const parseMonthYear = monthYear => {
        const [monthAbbr, yearSuffix] = monthYear.split('-');
        const fullYear = 2000 + parseInt(yearSuffix, 10);

        const monthIndex = monthNames.indexOf(monthAbbr);
        if (monthIndex === -1) return 0;

        return fullYear * 12 + monthIndex;
      };

      return parseMonthYear(a) - parseMonthYear(b);
    });

    const ltlData = allMonths.map(month =>
      Math.round(dataByMonthType[month]?.LTL || 0)
    );
    const tlData = allMonths.map(month =>
      Math.round(dataByMonthType[month]?.TL || 0)
    );

    const parcelData = allMonths.map(month =>
      Math.round(dataByMonthType[month]?.Parcel || 0)
    );

    const getSeriesData = () => {
      const series = [];
      const currentFilter = filters.shipmentType;
      const includeAll = currentFilter === 'All';

      if (includeAll || currentFilter === 'LTL') {
        series.push({ name: 'LTL', data: ltlData });
      }
      if (includeAll || currentFilter === 'Parcel') {
        series.push({ name: 'Parcel', data: parcelData });
      }

      if (includeAll || currentFilter === 'TL') {
        series.push({ name: 'TL', data: tlData });
      }

      return series;
    };

    return {
      categories: allMonths,
      series: getSeriesData()
    };
  }, [shipments, filters.shipmentType]);

  const weeks = getUniqueWeeks();

  // Update resetFilters function to include new filter states
  const resetFilters = () => {
    setFilters({
      customer: 'All',
      originState: 'All',
      destinationState: 'All',
      shipmentType: 'All',
      shipmentTypeRule: 'All',
      carrier: 'All',
      dateRange: 'custom',
      startDate: new Date('2024-10-28').toISOString().split('T')[0],
      endDate: new Date('2026-01-21').toISOString().split('T')[0],
      week: 'All',
      selectedMonth: 'All',
      selectedWeightRange: 'All',
      selectedMileRange: 'All',
      selectedCarrier: 'All',
      selectedShipmentType: 'All'
    });

    setShipments(shipsData);

    setBookedShipments(originalBookedShipments); // You'll need to create this state
  };

  // Calculate statistics
  const totalFreightSpent = shipments.reduce(
    (sum, shipment) => sum + (shipment.quoteNetCharge || 0),
    0
  );

  const netFreightSpent = Math.max(0, totalFreightSpent);

  const totalShipments = shipments.length;

  const capitalize = str =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  // Get unique values for filters
  const customers = [
    'All',
    ...new Set(shipsData.map(s => s.destinationCompany).filter(Boolean))
  ];
  const originStates = [
    'All',
    ...new Set(shipsData.map(s => s.originState).filter(Boolean))
  ];
  const destinationStates = [
    'All',
    ...new Set(shipsData.map(s => s.destinationState).filter(Boolean))
  ];
  const shipmentTypes = [
    'All',
    ...new Set(shipsData.map(s => s.shipmentType).filter(Boolean))
  ];
  const shipmentModes = [
    'All',
    ...[
      ...new Set(
        shipsData
          .map(s => s.shipmentTypeRule)
          .filter(Boolean)
          .map(v => String(v).trim().toLowerCase())
      )
    ].map(capitalize)
  ];

  const carriers = [
    'All',
    ...new Set(shipsData.map(s => s.carrierName).filter(Boolean))
  ];

  const freightByShipMode = shipments.reduce((acc, shipment) => {
    if (shipment.shipmentTypeRule) {
      const raw = String(shipment.shipmentTypeRule).trim().toLowerCase();
      const mode = capitalize(raw);

      if (!acc[mode]) {
        acc[mode] = 0;
      }

      acc[mode] += shipment.quoteNetCharge || 0;
    }
    return acc;
  }, {});

  if (Object.keys(freightByShipMode).length === 0) {
    freightByShipMode['Outbound'] = 0;
    freightByShipMode['Inbound'] = 0;
    freightByShipMode['Transfer'] = 0;
    freightByShipMode['Dropship'] = 0;
  }

  const monthlyFreight = shipments.reduce((acc, shipment) => {
    if (shipment.actualPickupDate) {
      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!acc[monthYear]) {
        acc[monthYear] = 0;
      }
      acc[monthYear] += shipment.quoteNetCharge || 0;
    }
    return acc;
  }, {});

  const monthlyData = Object.entries(monthlyFreight)
    .map(([monthYear, freight]) => {
      const [monthAbbr, year] = monthYear.split('-');
      const date = new Date(`01 ${monthAbbr} 20${year}`);
      return { month: monthYear, freight, date };
    })
    .sort((a, b) => a.date - b.date)
    .map(({ month, freight }) => ({ month, freight }));

  const shipmentCountsByType = shipments.reduce((acc, shipment) => {
    if (shipment.shipmentTypeRule) {
      const raw = String(shipment.shipmentTypeRule).trim().toLowerCase();
      const mode = capitalize(raw);

      if (!acc[mode]) {
        acc[mode] = 0;
      }
      acc[mode] += 1;
    }
    return acc;
  }, {});

  if (Object.keys(freightByShipMode).length === 0) {
    freightByShipMode['Outbound'] = 0;
    freightByShipMode['Inbound'] = 0;
    freightByShipMode['Transfer'] = 0;
    freightByShipMode['Dropship'] = 0;

    shipmentCountsByType['Outbound'] = 0;
    shipmentCountsByType['Inbound'] = 0;
    shipmentCountsByType['Transfer'] = 0;
    shipmentCountsByType['Dropship'] = 0;
  }

  const shipTypePieSeries = Object.values(freightByShipMode);

  const shipTypePieOptions = {
    chart: {
      type: 'donut',
      height: expandedChart === 'shipmentTypePie' ? 200 : 300
    },
    labels: Object.keys(freightByShipMode),
    colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560'],
    legend: {
      position: expandedChart === 'shipmentTypePie' ? 'top' : 'bottom'
    },
    plotOptions: {
      pie: {
        donut: {
          size: expandedChart === 'shipmentTypePie' ? '70%' : '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 'bold',
              offsetY: -5
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
              offsetY: 10,
              formatter: function (val) {
                const inThousands = val / 1000;
                return '$' + Math.round(inThousands).toLocaleString() + 'K';
              }
            },
            total: { show: false }
          }
        },
        expandOnClick: false,
        dataLabels: {
          offset: expandedChart === 'shipmentTypePie' ? 68 : 27,
          minAngleToShowLabel: 0
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, { seriesIndex, w }) {
        const value = w.config.series[seriesIndex];
        const inThousands = value / 1000;

        if (value === 0) return '$0';

        return '$' + Math.round(inThousands).toLocaleString() + 'K';
      },
      style: {
        fontSize: expandedChart === 'shipmentTypePie' ? '13px' : '11px',
        fontWeight: 'bold',
        colors: ['#1a237e']
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        padding: expandedChart === 'shipmentTypePie' ? 2 : 4,
        borderRadius: 2,
        borderWidth: 0,
        opacity: 0.9
      },
      dropShadow: { enabled: false }
    },
    stroke: { show: true, width: 3 },
    tooltip: {
      y: {
        formatter: function (val, { seriesIndex, w }) {
          const totalFreight = Object.values(freightByShipMode).reduce(
            (a, b) => a + b,
            0
          );
          const percentage =
            totalFreight > 0 ? ((val / totalFreight) * 100).toFixed(1) : '0';

          return `Freight Spent: $${val.toLocaleString()} (${percentage}%)<br/>`;
        }
      }
    }
  };

  const totalPoundsMoved = shipments.reduce(
    (sum, item) => sum + (item.totalNetWeight || 0),
    0
  );

  const costPerPound = netFreightSpent / totalPoundsMoved;

  const formatToMillions = (
    value,
    isCurrency = false,
    isTotalFreightSpend = false
  ) => {
    if (value == null || isNaN(value)) return isCurrency ? '$0' : '0';

    if (isTotalFreightSpend) {
      if (value >= 1_000_000) {
        const rounded = Math.round(value / 1_000_000);
        return isCurrency ? `$${rounded}M` : `${rounded}M`;
      }

      if (value >= 1_000) {
        const rounded = Math.round(value / 1_000);
        return isCurrency ? `$${rounded}K` : `${rounded}K`;
      }

      return isCurrency
        ? `$${Math.round(value).toLocaleString()}`
        : Math.round(value).toLocaleString();
    }

    if (value >= 1_000_000) {
      const millions = value / 1_000_000;
      const formatted =
        millions >= 10 ? millions.toFixed(2) : millions.toFixed(1);
      return isCurrency ? `$${formatted}M` : `${formatted}M`;
    }

    if (value >= 1_0000) {
      const thousands = Math.floor(value / 1_000);
      return isCurrency ? `$${thousands}K` : `${thousands}K`;
    }

    return isCurrency
      ? `$${Math.round(value).toLocaleString()}`
      : value.toLocaleString();
  };

  const statCards = [
    {
      title: '# of Shipments',
      value: formatToMillions(totalShipments, false),
      tooltip: totalShipments.toLocaleString(),
      icon: <LocalShipping />,
      color: 'success',
      subtitle: 'All shipments'
    },
    {
      title: 'Total Freight Spent',
      value: formatToMillions(netFreightSpent, true, true),
      tooltip: `$${netFreightSpent.toLocaleString()}`,
      icon: <AttachMoney />,
      color: 'primary',
      subtitle: 'Total spent'
    },
    {
      title: '# of pounds moved',
      value: formatToMillions(totalPoundsMoved, false),
      tooltip: totalPoundsMoved.toLocaleString(),
      icon: <Scale />,
      color: 'warning',
      subtitle: 'Total weight'
    },
    {
      title: 'Cost per Pound',
      value: `$${costPerPound.toFixed(3)}`,
      tooltip: `$${costPerPound.toLocaleString(undefined, {
        minimumFractionDigits: 3
      })}`,
      icon: <Balance />,
      color: 'info',
      subtitle: 'Per pound cost'
    }
  ];

  // Add these optimized calculations before your return statement
  const costPerPoundData = React.useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (
        !shipment.actualPickupDate ||
        !shipment.totalGrossWeight ||
        !shipment.quoteNetCharge
      )
        return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = { totalWeight: 0, totalCost: 0, date };
      }

      dataByMonth[monthYear].totalWeight += shipment.totalGrossWeight || 0;
      dataByMonth[monthYear].totalCost += shipment.quoteNetCharge || 0;
    });

    const monthYearEntries = Object.entries(dataByMonth).map(
      ([monthYear, data]) => ({
        monthYear,
        ...data
      })
    );

    monthYearEntries.sort((a, b) => a.date - b.date);

    if (monthYearEntries.length > 0) {
      const allMonths = [];
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const startDate = monthYearEntries[0].date;
      const endDate = monthYearEntries[monthYearEntries.length - 1].date;

      let currentDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        1
      );
      const endDateMonth = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        1
      );

      const existingMonths = new Set(
        monthYearEntries.map(item => item.monthYear)
      );

      while (currentDate <= endDateMonth) {
        const month = monthNames[currentDate.getMonth()];
        const year = currentDate.getFullYear().toString().slice(-2);
        const monthYear = `${month}-${year}`;

        if (!existingMonths.has(monthYear)) {
          monthYearEntries.push({
            monthYear,
            totalWeight: 0,
            totalCost: 0,
            date: new Date(currentDate)
          });
        }

        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      monthYearEntries.sort((a, b) => a.date - b.date);
    }

    return {
      categories: monthYearEntries.map(item => item.monthYear),
      data: monthYearEntries.map(item => {
        return item.totalWeight > 0 ? item.totalCost / item.totalWeight : 0;
      })
    };
  }, [shipments]);

  const avgCostPerShipmentData = React.useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.quoteNetCharge) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = { totalCost: 0, count: 0 };
      }

      dataByMonth[monthYear].totalCost += shipment.quoteNetCharge || 0;
      dataByMonth[monthYear].count += 1;
    });

    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, data]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return {
          monthYear,
          totalCost: data.totalCost,
          count: data.count,
          date
        };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => {
        return item.count > 0 ? item.totalCost / item.count : 0;
      })
    };
  }, [shipments]);

  // Calculate freight by weight ranges
  const calculateFreightByWeight = shipments => {
    const weightRanges = [
      { min: 0, max: 499, label: '<500' },
      { min: 500, max: 999, label: '500-999' },
      { min: 1000, max: 1999, label: '1,000-1,999' },
      { min: 2000, max: 4999, label: '2,000-4,999' },
      { min: 5000, max: 9999, label: '5,000-9,999' },
      { min: 10000, max: 19999, label: '10,000-19,999' },
      { min: 20000, max: 40000, label: '20,000-40,000' },
      { min: 40001, max: Infinity, label: '>40,000' }
    ];

    const freightByWeight = weightRanges.map(range => 0);

    shipments.forEach(shipment => {
      const weight = shipment.totalNetWeight || 0;
      const freight = shipment.quoteNetCharge || 0;

      for (let i = 0; i < weightRanges.length; i++) {
        if (weight >= weightRanges[i].min && weight <= weightRanges[i].max) {
          freightByWeight[i] += freight;
          break;
        }
      }
    });

    return freightByWeight;
  };

  // Fixed calculation function for freight by miles
  const calculateFreightByMiles = shipments => {
    const mileRanges = [
      { min: 0, max: 50, label: '0-49' },
      { min: 50, max: 100, label: '50-99' },
      { min: 100, max: 250, label: '100-249' },
      { min: 250, max: 500, label: '250-499' },
      { min: 500, max: 750, label: '500-749' },
      { min: 750, max: 1000, label: '750-999' },
      { min: 1000, max: 1500, label: '1,000-1,499' },
      { min: 1500, max: 2000, label: '1,500-1,999' },
      { min: 2000, max: 2500, label: '2,000-2,499' },
      { min: 2500, max: Infinity, label: '2,500+' }
    ];

    const freightByMiles = mileRanges.map(range => 0);

    shipments.forEach(shipment => {
      const METERS_PER_MILE = 1609.344;
      const miles = shipment.shipmentMiles
        ? (shipment.shipmentMiles / METERS_PER_MILE).toFixed(0)
        : 0;
      const freight = shipment.quoteNetCharge || 0;

      for (let i = 0; i < mileRanges.length; i++) {
        const range = mileRanges[i];
        const inRange =
          range.max === Infinity
            ? miles >= range.min
            : miles >= range.min && miles < range.max;

        if (inRange) {
          freightByMiles[i] += freight;
          break;
        }
      }
    });

    return freightByMiles;
  };

  const freightByWeightData = calculateFreightByWeight(shipments);
  const freightByMilesData = calculateFreightByMiles(shipments);

  const calculateCarrierData = shipments => {
    const carrierMap = {};

    shipments.forEach(shipment => {
      const carrierName = shipment.carrierName;
      const shipmentCharge = shipment.quoteNetCharge || 0;

      if (carrierName) {
        if (!carrierMap[carrierName]) {
          carrierMap[carrierName] = {
            totalFreight: 0,
            shipmentCount: 0
          };
        }
        carrierMap[carrierName].totalFreight += shipmentCharge;
        carrierMap[carrierName].shipmentCount += 1;
      }
    });

    const sortedCarriers = Object.entries(carrierMap).sort(
      ([, a], [, b]) => b.totalFreight - a.totalFreight
    );

    const carrierNames = sortedCarriers.map(([name]) => name);
    const carrierSpend = sortedCarriers.map(([, data]) => data.totalFreight);

    return {
      carrierNames,
      carrierSpend,
      carrierMap
    };
  };

  const { carrierNames, carrierSpend, carrierMap } =
    calculateCarrierData(shipments);

  const weightRangeNames = [
    '<500',
    '500-999',
    '1,000-1,999',
    '2,000-4,999',
    '5,000-9,999',
    '10,000-19,999',
    '20,000-40,000',
    '>40,000'
  ];

  const mileRangeNames = [
    '0-49',
    '50-99',
    '100-249',
    '250-499',
    '500-749',
    '750-999',
    '1,000-1,499',
    '1,500-1,999',
    '2,000-2,499',
    '2,500+'
  ];

  // Monthly shipment count data
  const monthlyShipmentCountData = useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = 0;
      }
      dataByMonth[monthYear] += 1;
    });

    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, count]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, count, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => item.count)
    };
  }, [shipments]);

  // Monthly shipments by type
  const monthlyShipmentTypeCountData = useMemo(() => {
    const dataByMonthType = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.shipmentType) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonthType[monthYear]) {
        dataByMonthType[monthYear] = { LTL: 0, TL: 0, Parcel: 0 };
      }

      const type = shipment.shipmentType;
      if (dataByMonthType[monthYear][type] !== undefined) {
        dataByMonthType[monthYear][type] += 1;
      }
    });

    const monthlyData = Object.entries(dataByMonthType)
      .map(([monthYear, types]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, ...types, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      series: [
        { name: 'LTL', data: monthlyData.map(item => item.LTL || 0) },
        { name: 'Parcel', data: monthlyData.map(item => item.Parcel || 0) },
        { name: 'TL', data: monthlyData.map(item => item.TL || 0) }
      ]
    };
  }, [shipments]);

  // Average Shipment Size (lbs) data
  const avgShipmentSizeData = useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.totalGrossWeight) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = { totalWeight: 0, count: 0 };
      }
      dataByMonth[monthYear].totalWeight +=
        parseFloat(shipment.totalGrossWeight) || 0;
      dataByMonth[monthYear].count += 1;
    });

    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, data]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, ...data, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => {
        return item.count > 0 ? item.totalWeight / item.count : 0;
      })
    };
  }, [shipments]);

  // Pounds Moved per Month data
  const poundsMovedMonthlyData = useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.totalNetWeight) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = 0;
      }
      dataByMonth[monthYear] += parseFloat(shipment.totalNetWeight) || 0;
    });

    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, weight]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, weight, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => item.weight)
    };
  }, [shipments]);

  // Process your shipments array to get top 5 carriers
  const normalizeCarrierName = name => {
    if (!name || typeof name !== 'string') return 'UNKNOWN';

    const cleaned = name.trim().toUpperCase();

    if (cleaned.includes('XPO')) return 'XPO';

    if (
      cleaned.includes('R AND L') ||
      cleaned.includes('R & L') ||
      cleaned.includes('R & L CARRIER') ||
      cleaned.includes('RL CARRIER')
    ) {
      return 'RL Carriers';
    }

    return cleaned
      .replace(/\s+/g, ' ')
      .replace(/,? INC\.?$/i, '')
      .replace(/,? LLC\.?$/i, '')
      .replace(/,? LTD\.?$/i, '');
  };

  const processTopCarriersData = shipments => {
    const carrierStats = shipments.reduce((acc, shipment) => {
      const carrierName = normalizeCarrierName(shipment.carrierName);

      if (!acc[carrierName]) {
        acc[carrierName] = {
          totalPounds: 0,
          shipmentCount: 0
        };
      }

      acc[carrierName].totalPounds += Number(shipment.totalGrossWeight) || 0;

      acc[carrierName].shipmentCount += 1;

      return acc;
    }, {});

    const sortedCarriers = Object.entries(carrierStats)
      .map(([carrierName, stats]) => ({
        carrierName,
        totalPounds: stats.totalPounds,
        shipmentCount: stats.shipmentCount
      }))
      .sort((a, b) => b.totalPounds - a.totalPounds)
      .slice(0, 5);

    return {
      carrierNames: sortedCarriers.map(c => c.carrierName),
      poundsData: sortedCarriers.map(c => c.totalPounds),
      shipmentsData: sortedCarriers.map(c => c.shipmentCount),
      details: sortedCarriers
    };
  };

  const topCarriersData = processTopCarriersData(shipments);

  const processTopLanesData = shipments => {
    const normalize = val => val.toString().trim().toUpperCase();

    const isValidCity = val => typeof val === 'string' && val.trim().length > 0;

    const isDelivered = status =>
      typeof status === 'string' &&
      ['DELIVERED'].includes(status.toUpperCase());

    const laneStats = shipments.reduce((acc, shipment) => {
      if (
        !isDelivered(shipment.shipmentStatus) ||
        !isValidCity(shipment.originCity) ||
        !isValidCity(shipment.destinationCity)
      ) {
        return acc;
      }

      const originCity = normalize(shipment.originCity);
      const destinationCity = normalize(shipment.destinationCity);

      const laneKey = `${originCity}__${destinationCity}`;

      if (!acc[laneKey]) {
        acc[laneKey] = {
          shipmentCount: 0,
          originCity,
          destinationCity
        };
      }

      acc[laneKey].shipmentCount += 1;

      return acc;
    }, {});

    const sortedLanes = Object.entries(laneStats)
      .map(([laneKey, stats]) => ({
        laneName: laneKey,
        displayName: `${stats.originCity} → ${stats.destinationCity}`,
        shipmentCount: stats.shipmentCount,
        originCity: stats.originCity,
        destinationCity: stats.destinationCity
      }))
      .sort((a, b) => b.shipmentCount - a.shipmentCount)
      .slice(0, 5);

    return {
      laneNames: sortedLanes.map(l => l.laneName),
      displayNames: sortedLanes.map(l => l.displayName),
      shipmentCounts: sortedLanes.map(l => l.shipmentCount),
      details: sortedLanes
    };
  };

  const topLanesData = processTopLanesData(shipments);

  const processShipmentsByWeight = shipments => {
    const weightRanges = {
      under500: 0,
      _500_999: 0,
      _1000_1999: 0,
      _2000_4999: 0,
      _5000_9999: 0,
      _10000_19999: 0,
      _20000_39000: 0,
      over40000: 0
    };

    shipments.forEach(shipment => {
      const weight = shipment.totalNetWeight || 0;

      if (weight < 500) {
        weightRanges.under500++;
      } else if (weight >= 500 && weight <= 999) {
        weightRanges._500_999++;
      } else if (weight >= 1000 && weight <= 1999) {
        weightRanges._1000_1999++;
      } else if (weight >= 2000 && weight <= 4999) {
        weightRanges._2000_4999++;
      } else if (weight >= 5000 && weight <= 9999) {
        weightRanges._5000_9999++;
      } else if (weight >= 10000 && weight <= 19999) {
        weightRanges._10000_19999++;
      } else if (weight >= 20000 && weight <= 39000) {
        weightRanges._20000_39000++;
      } else if (weight >= 40000) {
        weightRanges.over40000++;
      }
    });

    return [
      weightRanges.under500,
      weightRanges._500_999,
      weightRanges._1000_1999,
      weightRanges._2000_4999,
      weightRanges._5000_9999,
      weightRanges._10000_19999,
      weightRanges._20000_39000,
      weightRanges.over40000
    ];
  };

  const shipmentsByWeightData = processShipmentsByWeight(shipments);

  // Donut chart click handler
  const handleDonutClick = (event, chartContext, config) => {
    const clickedIndex = config.seriesIndex;
    const labels = ['Outbound', 'Inbound', 'Transfer', 'Dropship'];
    const selectedType = labels[clickedIndex];

    if (selectedType) {
      handleFilterChange('shipmentTypeRule', selectedType);
    }
  };

  const shipTypePieOptionsWithClick = {
    ...shipTypePieOptions,
    chart: {
      ...shipTypePieOptions.chart,
      events: {
        dataPointSelection: handleDonutClick
      }
    }
  };

  const getExportDataForCurrentTab = tabKey => {
    switch (tabKey) {
      case 'freightSpend':
        return shipments.map((s, id) => ({
          'Shipment ID': id,
          Customer: s.destinationCompany,
          Origin: `${s.originCity}, ${s.originState}`,
          Destination: `${s.destinationCity}, ${s.destinationState}`,
          Carrier: s.carrierName,
          'Shipment Type': s.shipmentType,
          'Shipment Mode': s.shipmentTypeRule,
          'Pickup Date': s.actualPickupDate,
          'Delivery Date': s.actualDeliveryDate,
          'Freight Spend': s.quoteNetCharge,
          'Weight (lbs)': s.totalGrossWeight,
          Miles: s.shipmentMiles ? (s.shipmentMiles / 1609.344).toFixed(0) : 0,
          Status: s.shipmentStatus
        }));

      case 'shipments':
        return shipments.map((s, id) => ({
          'Shipment ID': id,
          Customer: s.destinationCompany,
          Origin: `${s.originCity}, ${s.originState}`,
          Destination: `${s.destinationCity}, ${s.destinationState}`,
          Carrier: s.carrierName,
          'Shipment Type': s.shipmentType,
          'Shipment Mode': s.shipmentTypeRule,
          'Pickup Date': s.actualPickupDate,
          'Delivery Date': s.actualDeliveryDate,
          'Weight (lbs)': s.totalGrossWeight,
          Miles: s.shipmentMiles ? (s.shipmentMiles / 1609.344).toFixed(0) : 0,
          Status: s.shipmentStatus
        }));

      case 'origin':
        return shipments.map((s, id) => ({
          'Shipment ID': id,
          'Origin City': s.originCity,
          'Origin State': s.originState,
          'Origin Zip': s.originZip,
          'Origin Company': s.originCompany,
          'Pickup Date': s.actualPickupDate,
          'Shipment Type': s.shipmentType,
          'Shipment Mode': s.shipmentTypeRule,
          Carrier: s.carrierName,
          'Weight (lbs)': s.totalGrossWeight,
          'Freight Spend': s.quoteNetCharge
        }));

      case 'destination':
        return shipments.map((s, id) => ({
          'Shipment ID': id,
          'Destination City': s.destinationCity,
          'Destination State': s.destinationState,
          'Destination Zip': s.destinationZip,
          'Destination Company': s.destinationCompany,
          'Delivery Date': s.actualDeliveryDate,
          'Shipment Type': s.shipmentType,
          'Shipment Mode': s.shipmentTypeRule,
          Carrier: s.carrierName,
          'Weight (lbs)': s.totalGrossWeight,
          'Freight Spend': s.quoteNetCharge
        }));

      case 'onTimeDelivery':
        return shipments
          .filter(
            item =>
              item.actualDeliveryDate &&
              item.shipmentStatus === 'Delivered' &&
              item.shipmentTypeRule === 'Outbound' &&
              item.shipmentType === 'LTL'
          )
          .map((s, id) => ({
            'Shipment ID': id,
            Origin: `${s.originCity}, ${s.originState}`,
            Destination: `${s.destinationCity}, ${s.destinationState}`,
            Carrier: s.carrierName,
            'Scheduled Delivery': s.scheduledDeliveryDate,
            'Actual Delivery': s.actualDeliveryDate,
            'Delivery Status': s.shipmentStatus,
            'On Time':
              new Date(s.actualDeliveryDate) <=
              new Date(s.scheduledDeliveryDate)
                ? 'Yes'
                : 'No',
            'Days Late': Math.max(
              0,
              Math.ceil(
                (new Date(s.actualDeliveryDate) -
                  new Date(s.scheduledDeliveryDate)) /
                  (1000 * 60 * 60 * 24)
              )
            ),
            'Freight Spend': s.quoteNetCharge
          }));

      case 'weeklyTrend':
        return shipments.map((s, id) => ({
          'Shipment ID': id,
          Origin: `${s.originCity}, ${s.originState}`,
          Destination: `${s.destinationCity}, ${s.destinationState}`,
          Carrier: s.carrierName,
          'Pickup Date': s.actualPickupDate,
          'Delivery Date': s.actualDeliveryDate,
          'Shipment Type': s.shipmentType,
          'Shipment Mode': s.shipmentTypeRule,
          'Weight (lbs)': s.totalGrossWeight,
          'Freight Spend': s.quoteNetCharge,
          Week: s.actualPickupDate
            ? moment(s.actualPickupDate).format('YYYY-[W]WW')
            : ''
        }));

      case 'claims':
        return shipments
          .filter(s => s.shipmentStatus === 'Delivered' && s.quoteNetCharge > 0)
          .map((s, id) => ({
            'Shipment ID': id,
            Origin: `${s.originCity}, ${s.originState}`,
            Destination: `${s.destinationCity}, ${s.destinationState}`,
            Carrier: s.carrierName,
            'Delivery Date': s.actualDeliveryDate,
            'Shipment Value': s.quoteNetCharge,
            'Weight (lbs)': s.totalGrossWeight,
            'Potential Claim Value': (s.quoteNetCharge * 0.1).toFixed(2),
            'Days Since Delivery': s.actualDeliveryDate
              ? Math.ceil(
                  (new Date() - new Date(s.actualDeliveryDate)) /
                    (1000 * 60 * 60 * 24)
                )
              : ''
          }));

      default:
        return shipments;
    }
  };

  return (
    <>
      <MetaTags>
        <title>New Dashboard | TMS Shipping</title>
      </MetaTags>
      <DashboardContainer>
        {loading ? (
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '14vw'
            }}
          >
            <Grid sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant='body2' color='textSecondary'>
                Loading Dashboard...
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <>
            {/* Unified Expanded Chart Modal */}
            <Dialog
              fullScreen
              open={!!expandedChart}
              onClose={() => setExpandedChart(null)}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton
                    edge='start'
                    color='inherit'
                    onClick={() => setExpandedChart(null)}
                    aria-label='close'
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography
                    sx={{ ml: 2, flex: 1, color: '#ffffff !important' }}
                    variant='h6'
                    component='div'
                  >
                    {expandedChart === 'monthlyFreight' &&
                      'Monthly Freight Spend - Detailed View'}
                    {expandedChart === 'shipmentTypeMonthly' &&
                      'Monthly Freight by Shipment Type - Detailed View'}
                    {expandedChart === 'avgCostPerShipment' &&
                      'Average Cost per Shipment - Detailed View'}
                    {expandedChart === 'costPerPound' &&
                      'Cost per Pound - Detailed View'}
                    {expandedChart === 'carrierFreight' &&
                      'Freight Spent By Carrier - Detailed View'}
                    {expandedChart === 'freightByWeight' &&
                      'Freight Spend by Weight - Detailed View'}
                    {expandedChart === 'freightByMiles' &&
                      'Freight Spend by Miles - Detailed View'}
                    {expandedChart === 'shipmentTypePie' &&
                      'Freight Spend by Shipment Type - Detailed View'}
                    {expandedChart === 'originShipmentsMap' &&
                      'Origin Shipments Map - Detailed View'}
                    {expandedChart === 'destinationShipmentsMap' &&
                      'Destination Shipments Map - Detailed View'}
                    {expandedChart === 'monthlyShipments' &&
                      'Monthly Shipment Count - Detailed View'}
                    {expandedChart === 'shipmentTypeMonthlyCount' &&
                      'Monthly Shipments by Type - Detailed View'}
                    {expandedChart === 'avgShipmentSize' &&
                      'Average Shipment Size - Detailed View'}
                    {expandedChart === 'poundsMovedMonthly' &&
                      'Pounds Moved per Month - Detailed View'}
                    {expandedChart === 'shipmentsByTypeDonut' &&
                      '# of Shipments by Shipments Type - Detailed View'}
                    {expandedChart === 'topCarriers' &&
                      'Top 5 Carriers - Detailed View'}
                    {expandedChart === 'topLanes' &&
                      'Top 5 Lanes - Detailed View'}
                    {expandedChart === 'shipmentsByWeight' &&
                      'Shipments By Weight - Detailed View'}
                  </Typography>
                </Toolbar>
              </AppBar>
              <Box sx={{ p: 3, height: 'calc(100vh - 64px)' }}>
                {/* Top 5 Carriers Chart Expanded */}

                {expandedChart === 'topCarriers' && (
                  <Box sx={{ height: '100%' }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'line',
                          height: '100%',
                          toolbar: { show: true },
                          events: {
                            dataPointSelection: (
                              event,
                              chartContext,
                              config
                            ) => {
                              const selectedCarrier =
                                topCarriersData.carrierNames[
                                  config.dataPointIndex
                                ];
                              if (selectedCarrier) {
                                handleCarrierFilter(selectedCarrier);
                                setExpandedChart(null);
                              }
                            }
                          }
                        },
                        stroke: { width: [0, 2] },
                        xaxis: {
                          categories: topCarriersData.carrierNames,
                          labels: {
                            style: { fontSize: '14px', fontWeight: 'bold' },
                            rotate: -45,
                            hideOverlappingLabels: false,
                            trim: false,
                            maxHeight: 80
                          },
                          title: {
                            text: 'Carriers',
                            style: { fontSize: '16px', fontWeight: 'bold' }
                          }
                        },
                        yaxis: [
                          {
                            seriesName: 'Pounds',
                            title: {
                              text: 'Total Pounds (K)',
                              style: { fontSize: '16px', fontWeight: 'bold' }
                            },
                            labels: {
                              formatter: function (val) {
                                return (val / 1000).toFixed(0) + 'K';
                              },
                              style: { fontSize: '14px' }
                            }
                          },
                          {
                            seriesName: 'Shipments',
                            opposite: true,
                            labels: {
                              formatter: function (val) {
                                return Math.round(val).toLocaleString();
                              },
                              style: { fontSize: '14px' }
                            }
                          }
                        ],
                        colors: ['#008FFB', '#FF4560'],
                        fill: { opacity: [0.85, 1] },
                        grid: { padding: { bottom: 60 } },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          y: {
                            formatter: function (
                              val,
                              { seriesIndex, dataPointIndex }
                            ) {
                              const carrierName =
                                topCarriersData.carrierNames[dataPointIndex];
                              if (seriesIndex === 0) {
                                return `${carrierName}<br/>Pounds: ${val.toLocaleString()} (${val / 1000}K)`;
                              }
                              if (seriesIndex === 1) {
                                return `${carrierName}<br/>Shipments: ${val.toLocaleString()}`;
                              }
                              return val;
                            }
                          }
                        },
                        legend: {
                          position: 'top',
                          horizontalAlign: 'center',
                          fontSize: '14px',
                          itemMargin: { horizontal: 20, vertical: 10 }
                        }
                      }}
                      series={[
                        {
                          name: 'Total Pounds',
                          type: 'column',
                          data: topCarriersData.poundsData
                        },
                        {
                          name: 'Shipments',
                          type: 'line',
                          data: topCarriersData.shipmentsData
                        }
                      ]}
                      height={500}
                    />
                  </Box>
                )}

                {/* Top 5 Lanes Chart Expanded */}
                {expandedChart === 'topLanes' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      height: '100%',
                      width: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        width: '15%',
                        minWidth: 200,
                        pr: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: 500,
                        mt: 1.5
                      }}
                    >
                      {topLanesData.displayNames.map((lane, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 90,
                            mb: 0
                          }}
                        >
                          <Typography
                            variant='body2'
                            sx={{
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#304758'
                            }}
                          >
                            {index + 1}. {lane}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ width: '85%', height: '100%' }}>
                      <ReactApexChart
                        options={{
                          chart: {
                            type: 'bar',
                            height: '100%',
                            toolbar: { show: true }
                          },
                          plotOptions: {
                            bar: {
                              horizontal: true,
                              barHeight: '40%',
                              borderRadius: 1,
                              dataLabels: { position: 'right' }
                            }
                          },
                          dataLabels: {
                            enabled: true,
                            formatter: function (val) {
                              return (
                                '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0' +
                                val.toLocaleString()
                              );
                            },
                            offsetX: 2,
                            style: {
                              fontSize: '14px',
                              colors: ['#304758'],
                              fontWeight: 'bold'
                            }
                          },
                          xaxis: {
                            labels: {
                              formatter: function (val) {
                                return val.toLocaleString();
                              },
                              style: { fontSize: '14px' }
                            }
                          },
                          yaxis: { show: false, labels: { show: false } },
                          colors: ['#FEB019'],
                          tooltip: {
                            y: {
                              formatter: function (val, { dataPointIndex }) {
                                const lane =
                                  topLanesData.details[dataPointIndex];
                                return `${lane.originCity} → ${lane.destinationCity}<br/>Shipments: ${val.toLocaleString()}`;
                              }
                            }
                          },
                          grid: {
                            padding: { left: 0, right: 4, top: 0, bottom: 2 },
                            yaxis: { lines: { show: false } }
                          },
                          title: {
                            text: 'Top 5 Shipping Lanes by Number of Shipments',
                            align: 'left',
                            style: { fontSize: '18px', fontWeight: 'bold' }
                          }
                        }}
                        series={[
                          {
                            name: 'Shipments',
                            data: topLanesData.shipmentCounts
                          }
                        ]}
                        type='bar'
                        height={550}
                      />
                    </Box>
                  </Box>
                )}

                {/* Shipments by Weight Chart Expanded */}
                {expandedChart === 'shipmentsByWeight' && (
                  <Box sx={{ height: '100%' }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: { show: true },
                          events: {
                            dataPointSelection: (
                              event,
                              chartContext,
                              config
                            ) => {
                              const selectedWeightRange =
                                weightRangeNames[config.dataPointIndex];
                              if (selectedWeightRange) {
                                handleWeightFilter(selectedWeightRange);
                                setExpandedChart(null);
                              }
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            distributed: true,
                            horizontal: true,
                            barHeight: '70%',
                            borderRadius: 4,
                            dataLabels: {
                              position: 'right',
                              hideOverflowingLabels: false
                            }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            const pad =
                              '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                            return pad + val.toLocaleString();
                          },
                          offsetX: 20,
                          style: {
                            fontSize: '14px',
                            colors: ['#304758'],
                            fontWeight: 'bold',
                            textShadow: '1px 1px 2px white'
                          },
                          background: { enabled: false }
                        },
                        xaxis: {
                          labels: {
                            formatter: function (val) {
                              return val.toLocaleString();
                            },
                            style: { fontSize: '14px' }
                          }
                        },
                        yaxis: {
                          labels: {
                            show: true,
                            minWidth: 100,
                            maxWidth: 120,
                            style: { fontSize: '14px', fontWeight: 'bold' }
                          }
                        },
                        legend: { show: false },
                        grid: {
                          padding: { left: 20, right: 40, top: 20, bottom: 20 }
                        },
                        colors: [
                          '#008FFB',
                          '#00E396',
                          '#FEB019',
                          '#FF4560',
                          '#775DD0',
                          '#3F51B5',
                          '#546E7A',
                          '#D4526E',
                          '#13d8aa'
                        ],
                        tooltip: {
                          y: {
                            formatter: function (val, { dataPointIndex, w }) {
                              const weightRanges = [
                                'Under 500 lbs',
                                '500-999 lbs',
                                '1,000-1,999 lbs',
                                '2,000-4,999 lbs',
                                '5,000-9,999 lbs',
                                '10,000-19,999 lbs',
                                '20,000-40,000 lbs',
                                'Over 40,000 lbs'
                              ];
                              const weightRange = weightRanges[dataPointIndex];
                              return `${weightRange}: ${val.toLocaleString()} shipments`;
                            }
                          }
                        },
                        labels: [
                          '<500 lbs',
                          '500-999 lbs',
                          '1,000-1,999 lbs',
                          '2,000-4,999 lbs',
                          '5,000-9,999 lbs',
                          '10,000-19,999 lbs',
                          '20,000-40,000 lbs',
                          '>40,000 lbs'
                        ]
                      }}
                      series={[
                        { name: 'Shipments', data: shipmentsByWeightData }
                      ]}
                      type='bar'
                      height={500}
                    />
                  </Box>
                )}

                {expandedChart === 'shipmentsByTypeDonut' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'donut',
                        height: '100%',
                        toolbar: { show: true },
                        events: { dataPointSelection: handleDonutClick }
                      },
                      plotOptions: {
                        pie: {
                          dataLabels: { offset: 65, minAngleToShowLabel: 0 },
                          donut: {
                            size: '65%',
                            labels: {
                              show: true,
                              name: {
                                show: true,
                                fontSize: '12px',
                                fontWeight: 'bold'
                              },
                              value: {
                                show: true,
                                fontSize: '16px',
                                fontWeight: 'bold',
                                formatter: function (val) {
                                  return val >= 1000
                                    ? (val / 1000).toFixed(1) + 'k'
                                    : val.toLocaleString();
                                }
                              }
                            }
                          }
                        }
                      },
                      dataLabels: {
                        style: {
                          fontSize: '10px',
                          fontWeight: 'bold',
                          colors: ['#1a237e']
                        },
                        enabled: true,
                        formatter: function (_val, opts) {
                          const actualValue =
                            opts.w.globals.series[opts.seriesIndex];
                          return actualValue.toLocaleString();
                        },
                        background: {
                          enabled: true,
                          foreColor: '#fff',
                          padding: 6,
                          borderRadius: 6,
                          opacity: 0.9
                        },
                        dropShadow: { enabled: false }
                      },
                      stroke: { show: true, width: 3 },
                      legend: {
                        position: 'top',
                        horizontalAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        markers: { width: 12, height: 12, radius: 6 },
                        itemMargin: { horizontal: 10, vertical: 5 }
                      },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, opts) {
                            const labels = [
                              'Outbound',
                              'Inbound',
                              'Transfer',
                              'Dropship'
                            ];
                            const label =
                              labels[opts.seriesIndex] ||
                              `Type ${opts.seriesIndex + 1}`;
                            return `${label}: ${val.toLocaleString()} shipments`;
                          }
                        }
                      },
                      labels: ['Outbound', 'Inbound', 'Transfer', 'Dropship'],
                      responsive: [
                        {
                          breakpoint: 480,
                          options: {
                            chart: { width: '100%' },
                            legend: { position: 'bottom' }
                          }
                        }
                      ]
                    }}
                    series={Object.values(shipmentCountsByType)}
                    type='donut'
                    height='100%'
                  />
                )}

                {expandedChart === 'monthlyShipments' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyShipmentCountData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return val / 1000 + 'k';
                          }
                          return val;
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyShipmentCountData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        tickAmount: 6,
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return (val / 1000).toFixed(1) + 'k';
                            }
                            return val;
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: ['#00E396'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return val.toLocaleString() + ' shipments';
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Shipment Count',
                        data: monthlyShipmentCountData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'shipmentTypeMonthlyCount' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyShipmentTypeCountData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return val / 1000 + 'k';
                          }
                          return val > 0 ? val : '';
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyShipmentTypeCountData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return (val / 1000).toFixed(1) + 'k';
                            }
                            return val > 0 ? val : '';
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: ['#008FFB', '#00E396', '#FF4560'],
                      legend: { position: 'top', fontSize: '16px' },
                      tooltip: {
                        y: {
                          formatter: function (val, { seriesIndex }) {
                            const type = ['LTL', 'Parcel', 'TL'][seriesIndex];
                            return (
                              type + ': ' + val.toLocaleString() + ' shipments'
                            );
                          }
                        }
                      }
                    }}
                    series={monthlyShipmentTypeCountData.series}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'avgShipmentSize' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              avgShipmentSizeData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return (val / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(val);
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: avgShipmentSizeData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return (val / 1000).toFixed(1) + 'k';
                            }
                            return Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: ['#775DD0'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return Math.round(val).toLocaleString() + ' lbs';
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Avg Shipment Size',
                        data: avgShipmentSizeData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'poundsMovedMonthly' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              poundsMovedMonthlyData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return Math.round(val / 1000) + 'k';
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: poundsMovedMonthlyData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            return (val / 1000000).toFixed(1) + 'M';
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: ['#FF4560'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return val.toLocaleString() + ' lbs';
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Pounds Moved',
                        data: poundsMovedMonthlyData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'shipmentTypePie' && (
                  <ReactApexChart
                    options={{
                      ...shipTypePieOptions,
                      chart: {
                        ...shipTypePieOptions.chart,
                        height: '95%',
                        toolbar: { show: true },
                        events: { dataPointSelection: handleDonutClick }
                      }
                    }}
                    series={shipTypePieSeries}
                    type='donut'
                    height='95%'
                  />
                )}

                {expandedChart === 'freightByWeight' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const weightRanges = [
                              '<500',
                              '500-999',
                              '1,000-1,999',
                              '2,000-4,999',
                              '5,000-9,999',
                              '10,000-19,999',
                              '20,000-40,000',
                              '>40,000'
                            ];
                            const selectedWeightRange =
                              weightRanges[config.dataPointIndex];
                            if (selectedWeightRange) {
                              handleWeightFilter(selectedWeightRange);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '80%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          if (val >= 1000) {
                            return pad + '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: { fontSize: '16px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '<500',
                          '500-999',
                          '1,000-1,999',
                          '2,000-4,999',
                          '5,000-9,999',
                          '10,000-19,999',
                          '20,000-40,000',
                          '>40,000'
                        ],
                        labels: {
                          style: { fontSize: '14px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, { dataPointIndex, w }) {
                            const weightRanges = [
                              '<500 lbs',
                              '500-999 lbs',
                              '1,000-1,999 lbs',
                              '2,000-4,999 lbs',
                              '5,000-9,999 lbs',
                              '10,000-19,999 lbs',
                              '20,000-40,000 lbs',
                              '>40,000 lbs'
                            ];
                            const weightRange = weightRanges[dataPointIndex];
                            return `${weightRange}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '<500',
                        '500-999',
                        '1,000-1,999',
                        '2,000-4,999',
                        '5,000-9,999',
                        '10,000-19,999',
                        '20,000-40,000',
                        '>40,000'
                      ]
                    }}
                    series={[
                      { name: 'Freight Spend', data: freightByWeightData }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'freightByMiles' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const mileRanges = [
                              '0-49',
                              '50-99',
                              '100-249',
                              '250-499',
                              '500-749',
                              '750-999',
                              '1,000-1,499',
                              '1,500-1,999',
                              '2,000-2,499',
                              '2,500+'
                            ];
                            const selectedMileRange =
                              mileRanges[config.dataPointIndex];
                            if (selectedMileRange) {
                              handleMileFilter(selectedMileRange);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '80%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          if (val >= 1000) {
                            return pad + '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: { fontSize: '16px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '0-49',
                          '50-99',
                          '100-249',
                          '250-499',
                          '500-749',
                          '750-999',
                          '1,000-1,499',
                          '1,500-1,999',
                          '2,000-2,499',
                          '2,500+'
                        ],
                        labels: {
                          style: { fontSize: '14px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, { dataPointIndex, w }) {
                            const mileRanges = [
                              '0-49 miles',
                              '50-99 miles',
                              '100-249 miles',
                              '250-499 miles',
                              '500-749 miles',
                              '750-999 miles',
                              '1,000-1,499 miles',
                              '1,500-1,999 miles',
                              '2,000-2,499 miles',
                              '2,500+ miles'
                            ];
                            const mileRange = mileRanges[dataPointIndex];
                            return `${mileRange}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '0-49',
                        '50-99',
                        '100-249',
                        '250-499',
                        '500-749',
                        '750-999',
                        '1,000-1,499',
                        '1,500-1,999',
                        '2,000-2,499',
                        '2,500+'
                      ]
                    }}
                    series={[
                      { name: 'Freight Spend', data: freightByMilesData }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'carrierFreight' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedCarrier =
                              carrierNames[config.dataPointIndex];
                            if (selectedCarrier) {
                              handleCarrierFilter(selectedCarrier);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '95%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          const millions = val / 1000000;
                          return (
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0$' +
                            millions.toFixed(1) +
                            'M'
                          );
                        },
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 20
                      },
                      xaxis: {
                        min: Math.max(...carrierSpend) * 0.0,
                        max: Math.max(...carrierSpend) * 1.1,
                        labels: {
                          formatter: function (val) {
                            const millions = val / 1000000;
                            return '$' + millions.toFixed(1) + 'M';
                          },
                          style: { fontSize: '14px' }
                        },
                        title: {
                          text: 'Freight Spend (Millions)',
                          style: { fontSize: '16px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: carrierNames,
                        labels: {
                          style: { fontSize: '14px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: 20, right: 20 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C',
                        '#F86624'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, { dataPointIndex, w }) {
                            const carrierName = w.config.labels[dataPointIndex];
                            const shipmentCount =
                              carrierMap[carrierName]?.shipmentCount || 0;
                            return `${carrierName}: $${val.toLocaleString()} (${shipmentCount} shipments)`;
                          }
                        }
                      },
                      labels: carrierNames
                    }}
                    series={[{ name: 'Freight Spend', data: carrierSpend }]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'monthlyFreight' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyData[config.dataPointIndex]?.month;
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return '$' + Math.round(val);
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyData.map(item => item.month),
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 15, right: 15 } },
                      colors: ['#008FFB'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return 'Total Freight: $' + val.toLocaleString();
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Freight Spend',
                        data: monthlyData.map(item => Math.round(item.freight))
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'shipmentTypeMonthly' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyShipmentTypeData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return val > 0 ? '$' + Math.round(val) : '';
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyShipmentTypeData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 15, right: 15 } },
                      colors: ['#008FFB', '#00E396', '#FF4560'],
                      legend: { position: 'top', fontSize: '14px' },
                      tooltip: {
                        y: {
                          formatter: function (val, { seriesIndex }) {
                            const type = ['LTL', 'TL', 'Parcel'][seriesIndex];
                            return type + ': $' + val.toLocaleString();
                          }
                        }
                      }
                    }}
                    series={monthlyShipmentTypeData.series}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'avgCostPerShipment' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              avgCostPerShipmentData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return '$' + Math.round(val).toLocaleString();
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: avgCostPerShipmentData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            return '$' + Math.round(val).toLocaleString();
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 15, right: 15 } },
                      colors: ['#775DD0'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return (
                              'Avg Cost: $' + Math.round(val).toLocaleString()
                            );
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Avg Cost per Shipment',
                        data: avgCostPerShipmentData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'costPerPound' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              costPerPoundData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: { position: 'top' }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return '$' + val.toFixed(3);
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: costPerPoundData.categories,
                        labels: {
                          style: { fontSize: '14px' },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            return '$' + val.toFixed(1);
                          },
                          style: { fontSize: '14px' }
                        }
                      },
                      grid: { padding: { left: 15, right: 15 } },
                      colors: ['#FF4560'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return 'Cost per Pound: $' + val.toFixed(2);
                          }
                        }
                      }
                    }}
                    series={[
                      { name: 'Cost per Pound', data: costPerPoundData.data }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}
              </Box>
            </Dialog>

            {/* Calculate available tabs and their indexes */}
            {(() => {
              // Build array of available tab keys and their display indexes
              const availableTabs = [];

              // Helper to add tab if permission exists
              const addTabIfAllowed = (level, key, icon, label) => {
                if (
                  userPermissionsArr?.filter(
                    permissions => permissions[`level${level}`]
                  ).length ||
                  UserIsAdmin
                ) {
                  availableTabs.push({ key, icon, label, level });
                }
              };
              addTabIfAllowed(
                1,
                'yoyFreight',
                <AttachMoney />,
                'YOY/YTD Freight'
              );
              // Check each permission level
              addTabIfAllowed(
                2,
                'freightSpend',
                <AttachMoney />,
                'Freight Spend'
              );
              addTabIfAllowed(
                3,
                'shipments',
                <LocalShipping />,
                '# of Shipments'
              );
              addTabIfAllowed(4, 'origin', <Place />, 'Origin');
              addTabIfAllowed(5, 'destination', <Map />, 'Destination');
              addTabIfAllowed(
                6,
                'onTimeDelivery',
                <Schedule />,
                'On-Time Delivery'
              );
              addTabIfAllowed(
                7,
                'weeklyTrend',
                <CalendarToday />,
                'Weekly Trend'
              );

              addTabIfAllowed(
                8,
                'palletScoreCard',
                <Inventory />,
                'Pallet Score Card'
              );
              addTabIfAllowed(9, 'claims', <GavelIcon />, 'Claims');

              // Get current tab key based on activeTab index
              const currentTab = availableTabs[activeTab] || availableTabs[0];

              return (
                <>
                  {/* Tabs Section */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 0.1,
                      position: 'absolute',
                      top: 20,
                      zIndex: 1,
                      width: '86.5%'
                    }}
                  >
                    <CompactTabs
                      value={activeTab}
                      onChange={(e, newValue) => setActiveTab(newValue)}
                      variant='scrollable'
                      scrollButtons='auto'
                    >
                      {availableTabs.map((tab, index) => (
                        <Tab
                          key={tab.key}
                          icon={tab.icon}
                          label={tab.label}
                          iconPosition='start'
                        />
                      ))}
                    </CompactTabs>
                  </Paper>

                  {/* Top Filters Section - Single Line (show for all tabs except Claims) */}
                  {currentTab &&
                    currentTab.key !== 'claims' &&
                    currentTab.key !== 'palletScoreCard' &&
                    currentTab.key !== 'yoyFreight' && (
                      <FilterBar
                        filters={filters}
                        customers={customers}
                        originStates={originStates}
                        destinationStates={destinationStates}
                        shipmentTypes={shipmentTypes}
                        shipmentModes={shipmentModes}
                        carriers={carriers}
                        weeks={weeks}
                        onFilterChange={handleFilterChange}
                        onReset={resetFilters}
                        exportData={getExportDataForCurrentTab(currentTab.key)}
                        allShipments={shipsData} // Pass the original unfiltered data
                        exportFilename={`${currentTab.key}_data`}
                        exportSheetName={currentTab.label}
                        showExport={true}
                        customDateRange={true}
                      />
                    )}

                  {/* Render Tab Content based on current tab key */}
                  <Box sx={{ mt: 1 }}>
                    {currentTab && (
                      <>
                        {/* Freight Spend Tab */}
                        {currentTab.key === 'yoyFreight' && (
                          <YOYFreightTab
                            allShipments={shipsData}
                            filters={filters}
                          />
                        )}
                        {currentTab.key === 'freightSpend' && (
                          <FreightSpendTab
                            shipments={shipments}
                            statCards={statCards}
                            monthlyData={monthlyData}
                            monthlyShipmentTypeData={monthlyShipmentTypeData}
                            carrierNames={carrierNames}
                            carrierSpend={carrierSpend}
                            carrierMap={carrierMap}
                            avgCostPerShipmentData={avgCostPerShipmentData}
                            costPerPoundData={costPerPoundData}
                            freightByWeightData={freightByWeightData}
                            freightByMilesData={freightByMilesData}
                            shipTypePieSeries={shipTypePieSeries}
                            shipTypePieOptionsWithClick={
                              shipTypePieOptionsWithClick
                            }
                            setExpandedChart={setExpandedChart}
                            handleMonthFilter={handleMonthFilter}
                            handleCarrierFilter={handleCarrierFilter}
                            handleWeightFilter={handleWeightFilter}
                            handleMileFilter={handleMileFilter}
                            weightRangeNames={weightRangeNames}
                            mileRangeNames={mileRangeNames}
                            expandedChart={expandedChart}
                          />
                        )}

                        {/* # of Shipments Tab */}
                        {currentTab.key === 'shipments' && (
                          <ShipmentsTab
                            shipments={shipments}
                            statCards={statCards}
                            monthlyShipmentCountData={monthlyShipmentCountData}
                            monthlyShipmentTypeCountData={
                              monthlyShipmentTypeCountData
                            }
                            shipmentCountsByType={shipmentCountsByType}
                            avgShipmentSizeData={avgShipmentSizeData}
                            poundsMovedMonthlyData={poundsMovedMonthlyData}
                            topCarriersData={topCarriersData}
                            topLanesData={topLanesData}
                            shipmentsByWeightData={shipmentsByWeightData}
                            weightRangeNames={weightRangeNames}
                            setExpandedChart={setExpandedChart}
                            handleMonthFilter={handleMonthFilter}
                            handleCarrierFilter={handleCarrierFilter}
                            handleWeightFilter={handleWeightFilter}
                            handleDonutClick={handleDonutClick}
                            expandedChart={expandedChart}
                          />
                        )}

                        {/* Origin Tab */}
                        {currentTab.key === 'origin' && (
                          <Box>
                            <OriginDestinationTab
                              shipments={shipments}
                              tabValue={2}
                            />
                          </Box>
                        )}

                        {/* Destination Tab */}
                        {currentTab.key === 'destination' && (
                          <Box>
                            <OriginDestinationTab
                              shipments={shipments}
                              tabValue={3}
                            />
                          </Box>
                        )}

                        {/* On-Time Delivery Tab */}
                        {currentTab.key === 'onTimeDelivery' && (
                          <Box>
                            <OnTimeDeliveryTab
                              shipments={shipments.filter(item => {
                                const basicCheck =
                                  item.actualDeliveryDate &&
                                  item.shipmentStatus === 'Delivered' &&
                                  item.shipmentTypeRule === 'Outbound' &&
                                  item.shipmentType === 'LTL';

                                if (!basicCheck) return false;

                                if (filters?.startDate && filters?.endDate) {
                                  return isDateInRangeEST(
                                    item.actualDeliveryDate,
                                    filters.startDate,
                                    filters.endDate
                                  );
                                }

                                return true;
                              })}
                              lateShipsCol={
                                userPermissionsArr?.filter(
                                  permissions => permissions.level6
                                ).length || UserIsAdmin
                              }
                              viewDetails={
                                userPermissionsArr?.filter(
                                  permissions => permissions.level7
                                ).length || UserIsAdmin
                              }
                            />
                          </Box>
                        )}
                        {/* Pallet Score Card Tab */}
                        {/* <PalletScoreCard
                              shipments={bookedShipments}
                              setExpandedChart={setExpandedChart}
                              expandedChart={expandedChart}
                            /> */}
                        {currentTab.key === 'palletScoreCard' && (
                          <Box>
                            <PalletLocationBreakdown
                              shipments={bookedShipments}
                            />
                          </Box>
                        )}

                        {/* Weekly Trend Tab */}
                        {currentTab.key === 'weeklyTrend' && (
                          <Box>
                            <WeeklyTrendsTab shipments={shipments} />
                          </Box>
                        )}

                        {/* Claims Tab */}
                        {currentTab.key === 'claims' && (
                          <Box>
                            <ClaimsDashboard shipments={shipments} />
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </>
              );
            })()}
          </>
        )}
      </DashboardContainer>
    </>
  );
};

export default TMSDashboard;
